// THROWAWAY POC (RevoGrid evaluation) — route /tag-config-demo.
// Delete this folder + the route entry in src/routes/index.tsx to remove.
//
// ============================================================================
// PART 6 — getting the dropdown column into the range paths
// ============================================================================
// PART 5's two bulk behaviours worked on text columns but the dropdown column
// only half took part. Three separate causes, none of them in SelectCellEditor
// — the editor emits exactly what TextEditor does, `save(value)` → `celledit`
// with the option string, and copy already read the model value correctly.
//
// (1) COPY→PASTE INTO A DROPDOWN RANGE was already working for a real option.
//     copyRangeArray() reads `item[prop]` off the model, so Ctrl+C on a dropdown
//     cell puts "Option A" on the clipboard and the paste fills the range like
//     any text column. Measured before touching anything.
//     WHAT WAS ACTUALLY BROKEN: clearing. sanitizeOptionValues() treated '' as
//     an out-of-list value, so it silently swallowed every attempt to empty a
//     dropdown cell in bulk — copying an empty cell and pasting it, and Delete
//     over a range (which routes through getRangeStaticData(range, '')). Text
//     columns cleared fine, so the dropdown column was the only one that could
//     not be emptied. Fixed by treating '' as valid; non-empty out-of-list
//     values are still dropped, which is the check that matters.
//
// (2) PICKING A VALUE OVER A SELECTED RANGE did not fan out, for two reasons
//     that had to be fixed together — either one alone still fails:
//     a. The chevron opens the editor with setCellEdit(), which writes the edit
//        cell straight into the selection store and never reaches revo-grid's
//        `onSetedit` — so `beforeeditstart` is never emitted and the fan-out was
//        never armed. handleOpenEditor now arms it directly, BEFORE calling
//        setCellEdit(), because setCellEdit() focuses the cell and that collapses
//        the live range to 1x1.
//     b. Even when it did arm (type a character first, then pick), the arm was
//        destroyed before the save. The MUI <Select> menu is a portal on
//        document.body, so clicking a MenuItem is a "mouseup outside the grid"
//        and RevoGrid answers it with clearFocus() → `beforefocuslost`, which
//        used to clear the arm. handleBeforeFocusLost no longer touches it; see
//        the comment there for why that cannot go stale.
//     Also: a dropdown column has no "type a value" gesture at all, so requiring
//     a keystroke would permanently exclude it. armRangeFanOut() therefore treats
//     ANY editor open in an OPTION_COLUMNS column as eligible, while text columns
//     still require a keystroke — so text double-click stays single-cell.
//
// CONSEQUENCES WORTH KNOWING
//   - Picking a value while a range spanning BOTH a dropdown and a text column
//     is selected writes that option into the text cells too. Same semantics as
//     type-to-overwrite; the fan-out follows the selection, not the column.
//   - To edit one dropdown cell, select just that cell — exactly like Excel.
//     Verified: with no range, picking writes one cell.
//   - Nothing was added to SelectCellEditor.tsx. Both fixes reuse the existing
//     mechanisms (the `rangeeditapply` dispatch from PART 5 and the clipboard
//     rangeFill from PART 5), so there is still only one bulk write path.
//
// ============================================================================
// PART 5 — native Excel-like bulk editing over a selected range
// ============================================================================
// The bulk-edit toolbar from PART 4 (a "Value" field + "Apply to selection"
// button calling setDataAt() in a loop) has been DELETED. It was an
// out-of-grid workaround; what follows is what RevoGrid does natively.
//
// #2 PASTE-INTO-RANGE — WORKS NATIVELY, but not on the default settings
//   `useClipboard` defaults to the boolean `true`, and that is NOT enough:
//   isClipboardRangeFillEnabled() requires an OBJECT with rangeFill === true
//   (`typeof useClipboard === 'object' && useClipboard.rangeFill === true`).
//   With the boolean default, pasting a 1x1 payload over a 3x2 selection writes
//   the focused cell only — measured. Passing `useClipboard={{ rangeFill: true }}`
//   makes onPaste() hand the dragged range to getTransformedDataToApply() as
//   `targetRange`, which tiles the single value across it. Verified:
//     - Ctrl+C one cell, drag a 3x1 range, Ctrl+V  → all 3 written
//     - OS-clipboard text, drag a 3x2 range, Ctrl+V → all 6 written
//     - shift+click range → same
//     - a 2x2 clipboard payload still tiles 2x2 from the focused cell and does
//       NOT stretch to fill a 3x2 selection (rangeFill is single-cell-only, by
//       design — getRangeFillClipboardData returns null for anything bigger)
//     - single focused cell, no range → writes that cell only
//     - a range that includes the readonly checkbox column skips it, because
//       applyClipboardRow() checks isReadOnly() per cell. No guard needed.
//   It routes through `beforerangeedit`, so handleBeforeRangeEdit's option-list
//   sanitiser already covers it — pasting "banana" over the dropdown column is
//   dropped, pasting "Option B" goes through. Both verified.
//
// #1 TYPE-TO-OVERWRITE A RANGE — NOT NATIVE. CUSTOM, ~40 LINES, BUILT BELOW.
//   Nothing on RevoGrid's edit path is range-aware. Typing a printable character
//   reaches KeyboardService.keyDown() → `sv.change(e.key)` → doEdit(val), which
//   opens the editor on the FOCUSED cell; on save, cellEdit() calls
//   getSaveData(rgRow, rgCol, val) for that one cell and revo-grid's
//   onCellEdit() calls dataProvider.setCellData() once. Measured before the
//   change: drag a 3x2 range, type "Z", Enter → exactly one cell written.
//
//   THE IMPLEMENTATION, all inside the grid's own event flow (three props, no
//   React-level keydown handler anywhere — that is what PART 4 proved unusable):
//     1. `beforeapplyrange` keeps selectedRangeRef in sync. Needed because
//        `beforeedit` has to be answered synchronously and getSelectedRange() is
//        a Promise. `beforefocuslost` clears it.
//     2. `beforeeditstart` arms the fan-out, but ONLY if `detail.val` is a
//        non-empty string. That is the discriminator that keeps every other way
//        of editing untouched: a keystroke arrives as val === that character,
//        while Enter, double-click and the dropdown chevron all arrive as ''.
//     3. `beforeedit` — revo-grid's documented "use your own implementation"
//        opt-out — calls preventDefault() to drop the single-cell write, then
//        re-emits the value as a `rangeeditapply` CustomEvent on the grid host.
//        That is the exact event revogr-overlay-selection fires for a fill or a
//        paste, so revo-grid's onRangeEdit() picks it up, emits `beforerangeedit`
//        (→ the dropdown sanitiser runs) and calls setRangeData(). No write is
//        reimplemented and setDataAt() is not used anywhere.
//   Timing that makes it safe: `beforeedit` is emitted synchronously from
//   onCellEdit() BEFORE its `await timeout()`, so cancelling really does stop
//   setCellData(); and it fires before onEditCell() calls focusNext(), which is
//   what collapses the range.
//
//   ONE READONLY GOTCHA: setRangeData() does NOT check readonly — only the
//   clipboard path does, in applyClipboardRow(). So applyValueToRange() has to
//   skip readonly columns itself or `__selected` gets written into the row
//   models as a junk key. Verified it does not.
//
//   BEHAVIOUR THAT CHANGED AS A RESULT (nothing else did):
//     - `afteredit` now reports a fanned-out typed edit in the RANGE shape
//       ({ data, models, newRange, oldRange, type }) instead of the cell shape
//       ({ prop, rowIndex, val, ... }) — unavoidable, it is the shape
//       setRangeData's caller emits, and the same one paste and fill produce.
//       A plain single-cell edit still reports the cell shape.
//     - Post-edit focus is RevoGrid's, untouched: focusNext() moves down one row
//       and the range collapses to 1x1. Excel would keep the range selected;
//       matching that would mean overriding focus movement too, which is a
//       separate change and was left alone deliberately.
//
// ============================================================================
// PART 4 — plain mouse range selection vs. the fill handle
// ============================================================================
// Question asked: does click-and-drag across cells (nothing copied, no fill
// handle) select a rectangular range, and does the bulk-edit toolbar act on it?
//
// WHAT WAS ALREADY WORKING — no code needed
//   - Click+drag range selection itself is fully native and was never broken.
//     `range={true}` is all it takes; `canFocus` was already at its default of
//     true. Both mousedown-drag and the fill-handle drag enter the SAME
//     AutoFillService.selectionStart(), differing only by a `type` argument:
//     'Selection' for a bare cell mousedown, 'AutoFill' for the corner handle.
//     On mouseup clearAutoFillSelection() branches — 'AutoFill' goes to
//     applyRangeWithData() (copies values), 'Selection' to applyRangeOnly()
//     (sets the range, writes nothing). So drag-select cannot be "off" while
//     the fill handle works; they are the same code path.
//   - Both states are visibly highlighted: a dashed 1px rect while dragging
//     (revogr-temp-range, class `temp-bg-range Selection`, coloured by
//     --revo-grid-temporary-selection-border) and a solid border + 8% primary
//     fill after mouseup (--revo-grid-selection-border / -selection-bg). Both
//     custom properties are already fed from the MUI theme in revoGridMuiSx.
//   - The checkbox column's stopPropagation guards do NOT leak. They sit on the
//     <input> elements only, so dragging from any data column — including the
//     dropdown column — starts a range normally. Only a drag that begins on the
//     checkbox glyph itself is swallowed, which is what those guards are for.
//   - getSelectedRange() reports mouse-made ranges exactly like fill-handle
//     ones. It reads the focused store's `range` and does not record how the
//     range got there.
//
// WHY THE OUT-OF-GRID TOOLBAR APPROACH WAS ABANDONED (kept as a finding — the
// code these notes describe is gone, see PART 5)
//   Two document-level listeners in RevoGrid bite any control that lives OUTSIDE
//   the grid element. Neither has a prop, and both had to be worked around by
//   intercepting DOM events before they reached `document`:
//   - `mouseup` on document → mouseupHandle() → clearFocus() whenever the
//     pointer went up outside the grid. The dragged range was therefore already
//     gone by the time an "Apply" click handler ran. Workaround was
//     preventDefault() on the toolbar's mouseup — RevoGrid's own documented
//     opt-out ("To keep your elements from losing focus use mouseup/touchend
//     e.preventDefault()").
//   - `keydown` on document → KeyboardService.keyDown(), which only excludes
//     RevoGrid's own editor input (isEditInput → `.closest('.edit-input-wrapper')`)
//     and knows nothing about other inputs on the page. With a cell focused,
//     every character typed into an outside field was ALSO handed to the grid,
//     which called preventDefault() — so the field stayed empty while the grid
//     opened a cell editor. Ctrl+A re-selected the whole grid; Delete cleared
//     the range. Workaround was stopPropagation on the toolbar's keydown.
//   The two were a pair: keeping focus alive is what exposed the keyboard
//   hijack, because keyDown() bails early when nothing is focused. Anything
//   built outside the grid inherits both problems, which is the main argument
//   for driving bulk edits from inside the grid's own event flow instead.
//
// `onBeforerange` IS NOT THE MOUSE-SELECTION HOOK
//   It is emitted from revo-grid's `selectionchangeinit` listener, and
//   AutoFillService only emits that from applyRangeWithData() — the fill-handle
//   branch. A plain drag never reaches it, so listening on `onBeforerange` to
//   check whether drag-select works reports a false negative. The event that
//   fires for every range change, mouse or keyboard, is `beforeapplyrange`
//   (step 1 of triggerRangeEvent). Both are wired below so the difference is
//   visible in the console.
//
// STILL OPEN (same root cause, outside this part's scope)
//   Picking an option in the MUI <Select> editor leaves getFocused() null,
//   because the menu is a portal outside revo-grid and clicking a MenuItem is
//   another "mouseup outside" → clearFocus(). The fill handle therefore
//   disappears until the cell is clicked again. Pre-existing PART 3 behaviour,
//   not caused by the fixes above; it would need the same preventDefault()
//   treatment inside SelectCellEditor's menu.
//
// ============================================================================
// PART 3 — dropdown editor vs. bulk range editing
// ============================================================================
// The "Third" column (`unit`) now uses a real MUI <Select> as its cell editor,
// via the free wrapper's `Editor()` helper — see ./SelectCellEditor.tsx.
// Options: "Option A" / "Option B" / "Option C".
//
// NOTE ON WHICH COLUMN: the checkbox column shifted the on-screen positions, so
// "third" is taken as the column *labelled* Third (`unit`) rather than the third
// column from the left. It is also the right one semantically — `unit` is the
// rawUnit slot, an MUI Autocomplete in the real form.
//
// >>> The headline finding: the option list lives only in the editor. Every
// >>> bulk write path bypasses the editor completely, so NONE of them validate
// >>> against it. Verified by reading the 4.25.1 source, not just by eye:
//
// (Follow-up in PART 6: the sanitiser that backstops those paths was itself too
// strict — it counted '' as out-of-list, which made the dropdown column the only
// column that could not be cleared in bulk.)
//
//   - Fill handle (drag-to-copy): propagates a VALID value, but only by
//     accident. It copies the source cell's raw value verbatim; if that cell
//     was set through the dropdown the copy is in-list, and if it was not, the
//     invalid value spreads unchecked. There is no lookup against the option
//     list anywhere in the path. It routes through `beforerangeedit`, which is
//     cancelable AND whose `detail.data` is handed straight to setRangeData —
//     so mutating that detail is a real sanitisation hook. handleBeforeRangeEdit
//     uses it: dropping a prop leaves the target cell's old value intact.
//   - "Apply to selection" button (SINCE REMOVED — see PART 5): did NOT set a
//     valid value on its own. It called setDataAt(), which goes directly to
//     dataProvider.setCellData() and never emits `beforeedit` — so a
//     `beforeedit` guard would not have caught it either, and it needed its own
//     OPTION_COLUMNS check. Worth keeping as a warning about setDataAt(): it is
//     the one bulk write path that bypasses every validation hook. The paste
//     path that replaced it does not have this problem, because it goes through
//     `beforerangeedit` like the fill handle.
//   - Invalid/out-of-list state after a bulk op: reachable, and it fails quietly
//     rather than loudly. The cell renders the raw string like any other text,
//     so a bad value is invisible until the editor opens — at which point MUI
//     <Select> finds no matching MenuItem and shows an EMPTY box, silently
//     hiding the bad value instead of flagging it. Reopening and cancelling
//     leaves the bad value in place. The editor guards against the MUI warning
//     by falling back to '' when the value is out of list, so it cannot be told
//     apart from a genuinely empty cell. Real usage needs a validation pass over
//     the model, not just guards on the write paths.
//   - Clipboard paste has exactly the same exposure as the fill handle and is
//     covered by the same beforerangeedit hook.
//
// OTHER THINGS THIS SURFACED
//   - `Editor()` mounts the component in its own React root, which is OUTSIDE
//     the app's <ThemeProvider> — React context does not cross roots. Without a
//     bridge, MUI editors render with the DEFAULT MUI theme (Roboto, blue).
//     setEditorTheme() passes the live theme through a module-level variable;
//     there is no provider prop on the grid to do it properly.
//   - Cost is acceptable here in a way it was not for cell templates: only one
//     editor is open at a time, so it is one React root, not one per cell.
//   - `column.editor` accepts an EditorCtr directly, so no `editors` map needed.
//   - Range selection over the dropdown column needed no special handling.
//
// STYLING THE DROPDOWN TO MATCH MUI
//   - The open editor matches well. It is a genuine MUI <Select>, outlined,
//     size="small", 37px tall with the 16px cell gutter and 14px text — the
//     same geometry RHFSelect gets in EditGatewayTableRow. Because the bridged
//     theme carries the app's `components` overrides, the menu picks up
//     MuiPopover's customShadows.dropdown for free.
//   - The RESTING state needed solving separately, because RevoGrid only mounts
//     the editor while a cell is actually being edited — between edits the cell
//     is just text, whereas the reference form shows an outlined select in every
//     row, always. Fixed by drawing a STATIC REPLICA of MUI's small
//     OutlinedInput in the cellTemplate: same 37px height (FIELD_HEIGHT, shared
//     with the editor so they line up), 8px radius, divider border, 14px text,
//     ArrowDropDown at right: 7px. It is plain hyperscript — one div and one
//     svg — so it costs nothing per cell, unlike mounting a real MUI component.
//     The genuine <Select> takes over on edit and covers it.
//   - The catch with the replica: it is a picture of a select, not a select. It
//     has no hover/focus ring, no ripple, no disabled or error state, and it
//     will drift from MUI if the theme's input styling ever changes, because
//     the geometry is hand-copied rather than inherited. The chevron gets a
//     click handler so single-click opens the editor like a real select would;
//     the rest of the cell is left alone so click-drag range selection over
//     this column still works.
//
// ============================================================================
// PART 2 — row selection / bulk actions: native vs. custom
// ============================================================================
// Everything below runs on the free MIT @revolist/react-datagrid core. No Pro
// package, no new dependencies.
//
// NATIVE RevoGrid features (used as-is, no custom code)
//   - Inline cell editing on double-click / Enter, Esc to cancel. Untouched,
//     still works — requirement 7. The `__selected` column opts out via
//     `readonly: true`.
//   - Range selection + fill handle drag-to-copy (`range={true}`). Untouched,
//     still works — requirement 6. See PART 4 for how mouse drag-select and the
//     fill handle relate, and PART 5 for range-fill paste.
//   - Column resize (`resize={true}`).
//   - `cellTemplate` / `columnTemplate` hooks — the documented extension point
//     for custom cell and header content. Free tier.
//   - `additionalData` prop — the documented channel for pushing outside state
//     into templates. This is what keeps `columns` a stable module-level
//     reference (RevoGrid re-renders on new `additionalData` identity) instead
//     of rebuilding the column array on every selection change.
//   - Grid API: getSource(), getVisibleSource(), setDataAt(), setCellEdit(),
//     scrollToRow(), clearFocus(), refresh(). All public and free.
//
// CUSTOM CODE we had to write
//   - The whole checkbox column: renderer, header renderer, and the
//     click-to-toggle wiring. RevoGrid has no built-in row-selection column.
//   - Select-all / indeterminate header state, including the tri-state maths.
//   - `selectedIds` Set in React + pruning it when rows disappear.
//   - Bulk-actions toolbar, confirmation dialog, delete, and add-row — all
//     application-level, as expected.
//   - mousedown/click stopPropagation on both checkboxes. `revogr-data` is a
//     child of `revogr-overlay-selection`, so a bare click inside a cell also
//     starts a range selection and steals focus.
//
// WHERE THE FREE PACKAGE FELT LIMITING / NEEDED WORKAROUNDS
//   - No row-selection model at all. There is an internal `rowSelectionStore`
//     but it is not part of the public API, so selection state, the select-all
//     checkbox and the "which rows are visible" question are all ours.
//   - React is NOT the source of truth for cell values. Inline edits and
//     setDataAt() write straight into RevoGrid's internal store and never
//     reach React state. Any structural change (add/delete) therefore has to
//     re-read `await grid.getSource()` first and rebuild from that, or edits
//     are silently reverted — see mutateSource(). This is the sharpest edge we
//     hit and it would shape how we bind the real tag-config form.
//   - Templates are synchronous but `getVisibleSource()` is a Promise, so the
//     header's checked/indeterminate state has to be derived from the React
//     `source` array rather than from what the grid actually shows. Fine here
//     (no filter plugin), but with filtering enabled the two could drift.
//   - No way to style a whole selected row from outside the data. `rowClass`
//     reads a property off the row model, so highlighting selected rows would
//     mean writing a flag into every row object and re-seeding `source` on
//     each click — which resets focus. We left rows unhighlighted.
//   - `additionalData` has a @Watch that only re-emits an event; the actual
//     re-render relies on prop-identity change flowing down to `revogr-data`.
//     It works, but it is an implementation detail, so selection changes also
//     call refresh() explicitly as a belt-and-braces measure.
//   - Programmatic "start editing the row I just added" is a three-step async
//     dance (refresh → scrollToRow → setCellEdit) because the new `source`
//     prop reaches the virtual viewport a tick after React commits.
//   - Custom cells are Stencil hyperscript, not React, so a real MUI
//     <Checkbox> is not directly available. The wrapper does ship a
//     `Template()` helper that mounts a React component per cell, but that
//     means one React root per rendered cell — too heavy for a grid whose
//     selling point is virtualisation. We used a native checkbox with
//     `accentColor` instead, which is why it has no ripple and no MUI
//     indeterminate glyph.
//   - Deleting rows leaves RevoGrid's focus/selection pointing at indexes that
//     no longer exist, so clearFocus() has to be called manually.
//   - `readonly: true` is the only way to keep a column out of inline editing
//     and out of range-fill/paste writes (verified: isReadOnly() is checked in
//     every write path), but it also stamps a `disabled` class that paints the
//     cell with --rg-theme-cell-disabled-bg. Needed a cellProperties inline
//     style to undo the tint.
//
// ============================================================================
// PART 1 — MUI look-and-feel evaluation: what matched, what did not
// ============================================================================
// Setup: built-in `theme="material"` (`darkMaterial` in dark mode) + the
// `--revo-grid-*` custom properties fed from the live MUI theme in
// ./revoGridMuiSx.ts. Reference for the target look:
// src/features/gateway-details/components/EditGatewayDetailsForm.tsx.
//
// MATCHED EASILY (theme prop + CSS custom properties only)
//   - Font family / size. `material` already switches to a system stack and
//     left-aligned, non-uppercased headers; --revo-grid-font-family +
//     --revo-grid-font-size pick up 'Public Sans' and body2's 14px verbatim.
//   - Header row. --revo-grid-header-bg/-color/-height/-font-*/-text-transform
//     reproduce MuiTableCell `head` (background.neutral + text.secondary) and
//     the 48px height from AddGatewayTableHead 1:1.
//   - Borderless body rows. `material` does not paint --rg-theme-cell-border on
//     data cells at all, which happens to be exactly MUI's
//     `MuiTableCell.root.borderBottom: 'none'`. Free match.
//   - Cell + header padding via --revo-grid-cell-padding / -header-padding
//     (theme.spacing(0, 2) == MUI's 16px gutter).
//   - Primary accent everywhere it matters: focus border, selection range,
//     fill handle, filter-panel buttons. One --revo-grid-primary and it also
//     follows per-customer branding, since it reads palette.primary.main.
//   - Surface + text colours, so light/dark mode switching works for free.
//   - Filter panel colours (bg, border, input bg, icons) — a full set of
//     custom properties exists for it.
//   - Card frame (16px radius + customShadows.card): not a grid feature at
//     all — we set --revo-grid-header-border to transparent to kill RevoGrid's
//     own 1px box and let an MUI <Card> supply the frame. Clean result.
//
// DID *NOT* MATCH WELL
//   - Row hover. RevoGrid ships no hover rule for data rows (only for sortable
//     header cells), so there is no custom property for it. Hand-written
//     `.rgRow:hover .rgCell` CSS is needed, and because each pinned/unpinned
//     viewport section is a separate DOM subtree the highlight only covers the
//     section the pointer is over — MUI's full-width TableRow hover is not
//     reproducible without a JS row-index hover handler.
//   - Cell editors. The default editor is a bare native <input> with zero
//     shipped CSS and no custom properties. We can fake the outlined-input
//     geometry, but there is no label, no notched outline, no helper text and
//     no error state — nothing like the RHFTextField in EditGatewayTableRow.
//   - Dropdowns / selects. Corrected while building PART 3: it is not that the
//     built-in select is weak, it is that there is NO select editor at all. The
//     free bundle ships exactly one editor, TextEditor. (The stylesheet mentions
//     a `revo-dropdown` element, but no such component is in the package.) So a
//     dropdown means writing a custom editor — which PART 3 does, and it does
//     get real MUI components in. The gap that remains is validation, not looks.
//   - Elevation / shadows. Only one --revo-grid-shadow token, consumed by the
//     drag ghost and the filter panel. It is a flat colour, not a shadow, so
//     MUI's layered customShadows.card / .dropdown cannot be expressed.
//   - Ripple + transitions. No ripple anywhere and no transition tokens; MUI's
//     touch ripple and hover/focus easing are simply absent, so interactions
//     feel noticeably "snappier"/flatter than the rest of the app.
//   - Checkbox / icon cells. Renderers output raw HTML, so a "Hide" column
//     would need a custom cell renderer to look like the MUI Checkbox used in
//     EditGatewayDetailsForm. (Confirmed in part 2 — see the `Template()`
//     note above.)
//   - Border radius inside the grid. No radius tokens for cells, header or the
//     selection rectangle; only the outer Card can be rounded.
//   - Row height. Set through the `rowSize` prop (a number), not a custom
//     property, so it cannot follow theme.spacing.
//   - Scrollbars. RevoGrid uses its own virtual scroller and ignores the
//     theme.scrollbar tokens that MemoizedScrollbar applies elsewhere.
// ============================================================================
import { RevoGrid } from '@revolist/react-datagrid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import { revoGridMuiSx } from './revoGridMuiSx';
// FIELD_HEIGHT is shared so the resting-state replica and the real editor that
// covers it are exactly the same size
import { FIELD_HEIGHT, createSelectEditor, setEditorTheme } from './SelectCellEditor';

type TagRow = {
  id: string;
  name: string;
  details: string;
  unit: string;
  note: string;
};

/**
 * Stencil's hyperscript, typed locally: `@revolist/revogrid` is only a
 * transitive dependency (not hoisted under pnpm) so its types cannot be
 * imported directly. Structural types keep this compiling without adding a
 * package.
 */
type Hyper = (tag: string, props?: Record<string, unknown>, ...children: unknown[]) => unknown;

/** RevoGrid's RangeArea, typed locally for the same reason as `Hyper` above */
type RangeArea = { x: number; y: number; x1: number; y1: number };

/** a selection plus the dimension types every write API wants alongside it */
type SelectedRange = { range: RangeArea; rowType: string; colType: string };

/** the `beforeeditstart` / `beforeedit` detail, narrowed to what is used here */
type EditDetail = { rowIndex: number; colIndex: number; prop?: string; val?: unknown };

/** what armRangeFanOut needs, whether it came from an event or from the chevron */
type ArmArgs = { prop?: string; colIndex: number; rowIndex: number; val?: unknown };

const normalizeRange = (range: RangeArea) => ({
  xFrom: Math.min(range.x, range.x1),
  xTo: Math.max(range.x, range.x1),
  yFrom: Math.min(range.y, range.y1),
  yTo: Math.max(range.y, range.y1),
});

const isSingleCell = (range: RangeArea) => range.x === range.x1 && range.y === range.y1;

const rangeContains = (range: RangeArea, col: number, row: number) => {
  const { xFrom, xTo, yFrom, yTo } = normalizeRange(range);
  return col >= xFrom && col <= xTo && row >= yFrom && row <= yTo;
};

/** the slice of `additionalData` this page pushes into the templates */
type GridExtras = {
  selectedIds: Set<string>;
  allSelected: boolean;
  someSelected: boolean;
  accent: string;
  /** theme values the dropdown column's resting-state field is drawn from */
  field: {
    border: string;
    radius: string;
    bg: string;
    text: string;
    icon: string;
    height: string;
    fontSize: string;
  };
  onToggleRow: (id: string) => void;
  onToggleAll: () => void;
  /** opens the real MUI editor for a row — used by the chevron */
  onOpenEditor: (rowIndex: number) => void;
};

/** prop of the selection column — not real row data, never written to */
const SELECT_PROP = '__selected';

/**
 * The dropdown column. "Third" is the column labelled Third (`unit`) — the
 * checkbox column made it the fourth column on screen, so going by the header
 * label rather than the on-screen position. It is also the natural fit: this is
 * the rawUnit slot, which is an MUI Autocomplete in the real form.
 */
const UNIT_PROP = 'unit';
const UNIT_OPTIONS = ['Option A', 'Option B', 'Option C'] as const;
/** every prop whose value is constrained to a list, keyed by prop */
const OPTION_COLUMNS: Record<string, readonly string[]> = { [UNIT_PROP]: UNIT_OPTIONS };
const unitEditor = createSelectEditor(UNIT_OPTIONS);

/**
 * Drops any prop whose value is not in that column's option list. Returns how
 * many were dropped. Used as a backstop on the bulk paths, none of which run
 * through the editor.
 *
 * Empty counts as valid. An empty dropdown cell is a real state — it is what
 * every row starts as, and what the editor shows for "nothing selected" — so
 * `''` has to survive, otherwise the dropdown column becomes the only column in
 * the grid that cannot be CLEARED: Delete over a range routes through
 * getRangeStaticData(range, '') and pasting an empty source cell yields '', and
 * this guard was silently swallowing both. Non-empty out-of-list values are
 * still dropped, which is the check that actually matters.
 */
const isClearedValue = (value: unknown) =>
  value === '' || value === null || typeof value === 'undefined';

const sanitizeOptionValues = (row: Record<string, unknown>) => {
  let dropped = 0;
  Object.entries(OPTION_COLUMNS).forEach(([prop, allowed]) => {
    if (!(prop in row) || isClearedValue(row[prop])) {
      return;
    }
    if (!allowed.includes(row[prop] as string)) {
      delete row[prop];
      dropped += 1;
    }
  });
  return dropped;
};
/** the cell we drop the caret into when a new row is appended */
const FIRST_EDITABLE_PROP = 'name';

let rowCounter = 0;
const createRow = (): TagRow => {
  rowCounter += 1;
  return { id: `tag-${rowCounter}`, name: '', details: '', unit: '', note: '' };
};

const checkboxStyle = (accent: string) => ({
  width: '16px',
  height: '16px',
  margin: '0',
  cursor: 'pointer',
  accentColor: accent,
  verticalAlign: 'middle',
});

/**
 * note: columns & source need a "stable" reference in order to prevent infinite
 * re-renders. That is why every piece of live state the templates need arrives
 * through the third `additionalData` argument instead of being closed over.
 */
const columns = [
  {
    prop: SELECT_PROP,
    name: '',
    size: 56,
    // keeps the column out of inline editing and out of range-fill writes
    readonly: true,
    sortable: false,
    /**
     * `readonly` also makes RevoGrid stamp its `disabled` class on the cell,
     * which paints --rg-theme-cell-disabled-bg over it. Inline style wins over
     * that class, so this keeps the column looking like the rest of the grid.
     */
    cellProperties: () => ({ style: { backgroundColor: 'transparent' } }),
    /** requirement 2 — select/deselect every visible row */
    columnTemplate: (h: Hyper, _column: unknown, extra?: GridExtras) =>
      h(
        'span',
        { style: { display: 'flex', alignItems: 'center', height: '100%' } },
        h('input', {
          type: 'checkbox',
          'aria-label': 'Select all rows',
          checked: !!extra?.allSelected,
          // DOM property, not an attribute — Stencil assigns it directly
          indeterminate: !!extra?.someSelected && !extra?.allSelected,
          style: checkboxStyle(extra?.accent ?? ''),
          // the header cell dispatches `columnclick` on mousedown
          onMouseDown: (e: MouseEvent) => e.stopPropagation(),
          onClick: (e: MouseEvent) => e.stopPropagation(),
          onChange: () => extra?.onToggleAll(),
        })
      ),
    /** requirement 1 — per-row selection checkbox */
    cellTemplate: (h: Hyper, props: { model: TagRow }, extra?: GridExtras) => {
      const { id } = props.model;
      return h(
        'span',
        { style: { display: 'flex', alignItems: 'center', height: '100%' } },
        h('input', {
          type: 'checkbox',
          'aria-label': `Select row ${id}`,
          checked: !!extra?.selectedIds.has(id),
          style: checkboxStyle(extra?.accent ?? ''),
          // revogr-data sits inside revogr-overlay-selection, so an unguarded
          // click would also begin a range selection
          onMouseDown: (e: MouseEvent) => e.stopPropagation(),
          onClick: (e: MouseEvent) => e.stopPropagation(),
          onChange: () => extra?.onToggleRow(id),
        })
      );
    },
  },
  {
    prop: 'name',
    name: 'First',
  },
  {
    prop: 'details',
    name: 'Second',
  },
  {
    // requirement 1/2 — dropdown editor. `editor` accepts an EditorCtr
    // directly, so no `editors` map on the grid is needed. Range selection,
    // fill handle and clipboard paste all treat this like any other column,
    // which is exactly the problem documented in PART 3 above — the option list
    // lives only in the editor, so handleBeforeRangeEdit has to police it.
    prop: UNIT_PROP,
    name: 'Third',
    editor: unitEditor,
    // wider than RevoGrid's 100px default: the outlined field eats 32px of cell
    // gutter, a 14px left pad and a 24px chevron before any text shows
    size: 180,
    /**
     * Resting state. RevoGrid only mounts the editor while a cell is actually
     * being edited, so by default this column is indistinguishable from a text
     * column until you double-click it — whereas the reference form shows an
     * outlined select in every row, always.
     *
     * This draws a *static replica* of MUI's small OutlinedInput: same 37px
     * height, 8px radius, divider border, 14px text and the ArrowDropDown
     * glyph at right: 7px. It is plain hyperscript — a div and an svg, no MUI
     * component and no React root — so the always-visible look costs nothing
     * per cell. The real <Select> still takes over on edit and covers this.
     *
     * The chevron opens the editor on a single click, the way a real select
     * behaves. The rest of the cell is deliberately left alone so click-drag
     * range selection over this column still works.
     */
    cellTemplate: (h: Hyper, props: { model: TagRow; rowIndex: number }, extra?: GridExtras) => {
      const field = extra?.field;
      return h(
        'span',
        {
          style: {
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            width: '100%',
          },
        },
        h(
          'div',
          {
            style: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxSizing: 'border-box',
              width: '100%',
              height: field?.height ?? '37px',
              // MUI OutlinedInput small: 14px left gutter, icon sits at right: 7px
              padding: '0 7px 0 14px',
              border: `1px solid ${field?.border ?? 'transparent'}`,
              borderRadius: field?.radius ?? '8px',
              backgroundColor: field?.bg ?? 'transparent',
              color: field?.text ?? 'inherit',
              fontSize: field?.fontSize ?? 'inherit',
            },
          },
          h(
            'span',
            { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
            props.model[UNIT_PROP] ?? ''
          ),
          h(
            'span',
            {
              role: 'button',
              'aria-label': 'Open dropdown',
              style: {
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                cursor: 'pointer',
                color: field?.icon ?? 'currentColor',
              },
              // do not let the chevron start a range drag
              onMouseDown: (e: MouseEvent) => e.stopPropagation(),
              onClick: (e: MouseEvent) => {
                e.stopPropagation();
                extra?.onOpenEditor(props.rowIndex);
              },
            },
            h(
              'svg',
              { width: '24', height: '24', viewBox: '0 0 24 24', 'aria-hidden': 'true' },
              h('path', { fill: 'currentColor', d: 'M7 10l5 5 5-5z' })
            )
          )
        )
      );
    },
  },
  {
    prop: 'note',
    name: 'Fourth',
  },
];

/**
 * Column index of the dropdown column. The chevron opens the editor through
 * setCellEdit(), which reports no column index of its own, so the range check
 * for the fan-out has to look it up here.
 */
const UNIT_COL_INDEX = columns.findIndex((column) => column.prop === UNIT_PROP);

const initialSource: TagRow[] = Array.from({ length: 6 }, () => {
  const row = createRow();
  return { ...row, name: row.id.replace('tag-', ''), details: `Item ${rowCounter}` };
});

/** matches the `size="small"` rows of EditGatewayTableRow (37px input + cell padding) */
const ROW_SIZE = 52;
const HEADER_SIZE = 48;
/** keeps the grid a sensible height while rows are added and removed */
const MIN_VISIBLE_ROWS = 4;
const MAX_VISIBLE_ROWS = 10;

function App() {
  const [source, setSource] = useState<TagRow[]>(initialSource);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  /** row index queued for inline editing once the grid has rendered it */
  const [pendingEditRow, setPendingEditRow] = useState<number | null>(null);
  const gridRef = useRef<HTMLRevoGridElement>(null);
  /** set by beforerangeedit so the following beforeapplyrange keeps its message */
  const writeReportedRef = useRef(false);
  /**
   * Live mirror of the grid's selection, kept in sync by `beforeapplyrange` —
   * the single funnel every range change goes through (see PART 4). It exists
   * because `beforeedit` has to be answered SYNCHRONOUSLY to be able to cancel
   * the default single-cell write, and getSelectedRange() is a Promise.
   */
  const selectedRangeRef = useRef<SelectedRange | null>(null);
  /**
   * Armed by `beforeeditstart` when a printable character opened the editor over
   * a multi-cell range; read once by `beforeedit`. Null for every other way of
   * starting an edit, which is what keeps double-click / Enter / the dropdown
   * chevron on their original single-cell path.
   */
  const typedRangeEditRef = useRef<SelectedRange | null>(null);
  const [status, setStatus] = useState('');
  const theme = useTheme();

  /**
   * The dropdown editor renders in a detached React root and cannot read
   * context, so hand it the live theme. Safe in an effect: editors only mount
   * on user interaction, long after this has run.
   */
  useEffect(() => {
    setEditorTheme(theme);
  }, [theme]);

  const selectedCount = selectedIds.size;
  const allSelected = source.length > 0 && source.every((row) => selectedIds.has(row.id));
  const someSelected = selectedCount > 0;

  /**
   * Inline edits, fill-handle copies and clipboard pastes all land in
   * RevoGrid's internal store, never in React state, so any structural change
   * has to start from `getSource()` or it would roll those edits back.
   */
  const mutateSource = useCallback(
    async (mutate: (rows: TagRow[]) => TagRow[]) => {
      const grid = gridRef.current;
      const current = grid ? ((await grid.getSource()) as TagRow[]) : source;
      const next = mutate(current);
      setSource(next);
      return next;
    },
    [source]
  );

  /**
   * ---- RANGE FAN-OUT, step 1 of 2: decide whether this edit session counts.
   *
   * Called from EVERY path that opens an editor, and it always writes the ref —
   * either arming it or explicitly clearing it. That "always overwrite" property
   * is what makes a stale arm impossible, and it is why handleBeforeFocusLost
   * does not need to clear it (which it must not, see there).
   *
   * What makes an edit eligible:
   *
   *   - TEXT columns: only a keystroke. `val` is how the edit was opened — a
   *     printable character arrives as that character (KeyboardService calls
   *     `sv.change(e.key)`), while Enter and double-click arrive as '' (doEdit's
   *     default parameter). So double-click still edits exactly one cell.
   *   - OPTION (dropdown) columns: any editor open counts. There is no
   *     "type a value" gesture for a dropdown at all — the only way to set one is
   *     to pick from the list — so requiring a keystroke would leave the dropdown
   *     column permanently unable to take part. Picking IS the equivalent of
   *     typing here. To edit a single dropdown cell, select just that cell.
   */
  const armRangeFanOut = ({ prop, colIndex, rowIndex, val }: ArmArgs) => {
    const startedByTyping = typeof val === 'string' && val.length > 0;
    const isOptionColumn = !!prop && !!OPTION_COLUMNS[prop];
    const selection = selectedRangeRef.current;

    typedRangeEditRef.current =
      (startedByTyping || isOptionColumn) &&
      selection &&
      !isSingleCell(selection.range) &&
      rangeContains(selection.range, colIndex, rowIndex)
        ? selection
        : null;
  };

  /**
   * Single click on the chevron behaves like clicking a real MUI select.
   *
   * setCellEdit() writes the edit cell straight into the selection store, so
   * revo-grid never emits `beforeeditstart` for it and the fan-out would never be
   * armed. Arming here is what lets "select a range of dropdown cells, click one
   * chevron, pick a value" fill the range.
   */
  const handleOpenEditor = useCallback((rowIndex: number) => {
    armRangeFanOut({ prop: UNIT_PROP, colIndex: UNIT_COL_INDEX, rowIndex });
    void gridRef.current?.setCellEdit(rowIndex, UNIT_PROP);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleRow = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  /**
   * requirement 2 — "all currently visible rows". getVisibleSource() is the
   * honest answer (it respects trimming/filtering); `source` is the fallback
   * before the grid has mounted.
   */
  const handleToggleAll = useCallback(async () => {
    const grid = gridRef.current;
    const visible = grid ? ((await grid.getVisibleSource()) as TagRow[]) : source;
    const visibleIds = visible.map((row) => row.id);

    setSelectedIds((prev) => {
      const everyVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      if (everyVisibleSelected) {
        return new Set();
      }
      return new Set(visibleIds);
    });
  }, [source]);

  /** requirement 5 — actually remove the selected rows */
  const handleConfirmDelete = useCallback(async () => {
    const removed = selectedCount;
    await mutateSource((rows) => rows.filter((row) => !selectedIds.has(row.id)));
    setSelectedIds(new Set());
    setConfirmOpen(false);
    // focus/selection still points at indexes that no longer exist
    await gridRef.current?.clearFocus();
    setStatus(`Deleted ${removed} tag${removed === 1 ? '' : 's'}.`);
  }, [mutateSource, selectedCount, selectedIds]);

  /** requirement 8 — append an empty row and drop straight into editing it */
  const handleAddRow = useCallback(async () => {
    const next = await mutateSource((rows) => [...rows, createRow()]);
    setPendingEditRow(next.length - 1);
    setStatus('Added a new tag.');
  }, [mutateSource]);

  /**
   * The new `source` prop reaches the virtual viewport a tick after React
   * commits, so opening the editor needs its own pass once the row exists.
   */
  useEffect(() => {
    if (pendingEditRow === null) {
      return;
    }
    const grid = gridRef.current;
    if (!grid) {
      setPendingEditRow(null);
      return;
    }

    let cancelled = false;
    const openEditor = async () => {
      await grid.refresh();
      if (cancelled) {
        return;
      }
      await grid.scrollToRow(pendingEditRow);
      // the other setCellEdit() caller, and like the chevron it emits no
      // `beforeeditstart` — so disarm explicitly, or a range armed before "Add
      // new Tag" was clicked could still be sitting there when this row is saved
      typedRangeEditRef.current = null;
      await grid.setCellEdit(pendingEditRow, FIRST_EDITABLE_PROP);
      if (!cancelled) {
        setPendingEditRow(null);
      }
    };
    void openEditor();

    return () => {
      cancelled = true;
    };
  }, [pendingEditRow]);

  /** drop ids of rows that no longer exist so the counter cannot go stale */
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) {
        return prev;
      }
      const alive = new Set(source.map((row) => row.id));
      const next = new Set([...prev].filter((id) => alive.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [source]);

  /**
   * `additionalData` re-renders the templates through prop-identity change.
   * The explicit refresh() is a safety net — see the note at the top of the
   * file about that being an implementation detail rather than a contract.
   */
  useEffect(() => {
    void gridRef.current?.refresh();
  }, [selectedIds]);

  const gridExtras = useMemo<GridExtras>(
    () => ({
      selectedIds,
      allSelected,
      someSelected,
      accent: theme.palette.primary.main,
      field: {
        border: theme.palette.divider,
        radius: `${theme.shape.borderRadius}px`,
        bg: theme.palette.background.default,
        text: theme.palette.text.primary,
        icon: theme.palette.action.active,
        height: `${FIELD_HEIGHT}px`,
        fontSize: `${theme.typography.body2.fontSize}`,
      },
      onToggleRow: handleToggleRow,
      onToggleAll: handleToggleAll,
      onOpenEditor: handleOpenEditor,
    }),
    [
      selectedIds,
      allSelected,
      someSelected,
      theme,
      handleToggleRow,
      handleToggleAll,
      handleOpenEditor,
    ]
  );

  /**
   * The event that actually fires for a PLAIN MOUSE DRAG selection.
   *
   * `beforeapplyrange` is step 1 of triggerRangeEvent(), which is the single
   * funnel every range change goes through — mouse drag, shift+click, keyboard
   * shift+arrow and the fill handle's final apply. It is emitted by
   * revogr-overlay-selection rather than revo-grid, but it is bubbles+composed
   * so it reaches the host element and the React wrapper wires any unknown
   * `on*` prop straight to addEventListener (see attachProps in
   * @revolist/react-datagrid). That is why `onBeforeapplyrange` works even
   * though it is absent from the wrapper's typings — hence the cast on the prop.
   *
   * Do NOT use `onBeforerange` for this: see handleBeforeRange below.
   */
  const handleBeforeApplyRange = (
    event: CustomEvent<{ range: RangeArea; rowType: string; colType: string }>
  ) => {
    const range = event.detail?.range;
    if (!range) {
      return;
    }
    // feeds the synchronous mirror that type-to-overwrite reads — see
    // selectedRangeRef and handleBeforeEditStart
    selectedRangeRef.current = {
      range: { ...range },
      rowType: event.detail.rowType,
      colType: event.detail.colType,
    };
    const { x, y, x1, y1 } = range;
    const cols = Math.abs(x1 - x) + 1;
    const rows = Math.abs(y1 - y) + 1;
    // eslint-disable-next-line no-console
    console.log('[demo] beforeapplyrange — mouse/keyboard range selection', range);
    // A paste/fill also re-emits this right AFTER beforerangeedit (onRangeApply
    // calls rangeDataApply then setRange), so without this the "blocked N
    // values" message would always be overwritten by the range message.
    if (writeReportedRef.current) {
      writeReportedRef.current = false;
      return;
    }
    setStatus(`Range selected: ${rows}x${cols} (cols ${x}-${x1}, rows ${y}-${y1}).`);
  };

  /**
   * `onBeforerange` is NOT the mouse-selection hook, despite the name.
   *
   * revo-grid only emits it from its `selectionchangeinit` listener, and
   * AutoFillService emits that event from applyRangeWithData() — reached solely
   * when autoFillType === 'AutoFill', i.e. a fill-handle drag. A plain mouse
   * drag takes the sibling branch, applyRangeOnly(), which sets the range and
   * emits nothing of the kind. So this stays silent for drag-select and only
   * logs when the fill handle is used. Kept in to make that difference visible.
   */
  const handleBeforeRange = (event: CustomEvent<{ newRange: RangeArea }>) => {
    // eslint-disable-next-line no-console
    console.log('[demo] beforerange — FILL HANDLE only, never a plain drag', event.detail);
  };

  /**
   * `clearFocus()` drops focus and range together, so the mirror has to go with
   * them or a later drag-less edit could read a selection that no longer exists.
   *
   * It must NOT clear typedRangeEditRef though, and that is not an oversight —
   * it is the second reason the dropdown column could not fan out. The MUI
   * <Select> menu is a portal on document.body, so clicking a MenuItem is a
   * "mouseup outside the grid" and RevoGrid answers it with clearFocus(). That
   * lands BETWEEN arming the fan-out and the save that consumes it, so clearing
   * the arm here threw away the pick every single time.
   *
   * Safe to leave armed because armRangeFanOut() runs on every path that opens
   * an editor and always overwrites the ref, and handleBeforeEdit consumes it
   * one-shot — so no later edit can pick up a stale arm.
   */
  const handleBeforeFocusLost = () => {
    selectedRangeRef.current = null;
  };

  /**
   * `beforeeditstart` covers keystroke, Enter and double-click. It does NOT cover
   * the dropdown chevron: that goes through setCellEdit(), which writes the edit
   * cell straight into the selection store and never reaches revo-grid's
   * `onSetedit`, so no event is emitted. handleOpenEditor arms that path itself.
   */
  const handleBeforeEditStart = (event: CustomEvent<EditDetail>) => {
    const { val, rowIndex, colIndex, prop } = event.detail ?? {};
    armRangeFanOut({ prop, colIndex, rowIndex, val });
  };

  /**
   * ---- TYPE-TO-OVERWRITE, step 2 of 2: swap the single-cell write for a range
   * write.
   *
   * `beforeedit` is revo-grid's own documented opt-out — "To prevent the default
   * behavior of editing data and use your own implementation, call
   * e.preventDefault()" — and it is emitted synchronously from onCellEdit()
   * BEFORE its `await timeout()`, so cancelling here really does stop
   * dataProvider.setCellData(). It also fires before onEditCell() calls
   * focusNext(), which is what collapses the range to 1x1, so the selection is
   * still intact at this point.
   *
   * Two things have to be true for the swap, both already settled by
   * handleBeforeEditStart: the edit was opened by typing, and it happened inside
   * a multi-cell range. Otherwise this returns immediately and RevoGrid does
   * what it always did.
   */
  const handleBeforeEdit = (event: CustomEvent<EditDetail>) => {
    const pending = typedRangeEditRef.current;
    // one-shot: never let a stale arm leak into the next edit
    typedRangeEditRef.current = null;
    if (!pending) {
      return;
    }

    // cancel RevoGrid's single-cell write; the fan-out below replaces it
    event.preventDefault();
    void applyValueToRange(pending, event.detail?.val);
  };

  /**
   * Writes one value into every cell of `selection` through the SAME event
   * revogr-overlay-selection fires for a fill-handle drag or a paste:
   * `rangeeditapply`. revo-grid listens for it on its own host element, so
   * dispatching it there lands in onRangeEdit(), which emits `beforerangeedit`
   * (→ the dropdown sanitiser in handleBeforeRangeEdit runs) and then calls
   * dataProvider.setRangeData(). Nothing here reimplements the write.
   *
   * This is deliberately NOT setDataAt() in a loop — that was the removed
   * toolbar's approach and it is the one bulk path that skips every validation
   * hook (see PART 3).
   */
  const applyValueToRange = async (selection: SelectedRange, val: unknown) => {
    const grid = gridRef.current;
    if (!grid) {
      return;
    }

    const { range, rowType } = selection;
    const { xFrom, xTo, yFrom, yTo } = normalizeRange(range);
    // getColumns() is the honest column order (it accounts for flattening),
    // and getSource() supplies the `models` half of the event detail
    const [gridColumns, rows] = await Promise.all([grid.getColumns(), grid.getSource()]);

    const data: Record<number, Record<string, unknown>> = {};
    const models: Record<number, unknown> = {};

    for (let row = yFrom; row <= yTo; row++) {
      for (let col = xFrom; col <= xTo; col++) {
        const column = gridColumns[col];
        /**
         * setRangeData() does NOT check readonly — only the clipboard path does,
         * in applyClipboardRow(). So the checkbox column has to be skipped here
         * explicitly, or `__selected` would be written into the row models as a
         * junk property. Mirrors ColumnService.isReadOnly().
         */
        const readonly = column?.readonly;
        const isReadonly =
          typeof readonly === 'function'
            ? readonly({ model: rows[row], prop: column.prop, rowIndex: row, colIndex: col })
            : !!readonly;
        if (!column || isReadonly) {
          continue;
        }
        data[row] = data[row] ?? {};
        data[row][column.prop as string] = val;
        models[row] = rows[row];
      }
    }

    if (Object.keys(data).length === 0) {
      return;
    }

    grid.dispatchEvent(
      new CustomEvent('rangeeditapply', {
        detail: { data, models, type: rowType, newRange: range, oldRange: range },
        cancelable: true,
      })
    );
  };

  /**
   * Fill handle AND clipboard paste — including the new range-fill paste — all
   * land here, and mutating `detail.data` changes what gets written (RevoGrid
   * passes the same object straight to setRangeData). Dropping a prop leaves
   * that cell's previous value alone. This is the one sanitisation hook that
   * covers every bulk write path now that setDataAt() is gone.
   */
  const handleBeforeRangeEdit = (event: CustomEvent<{ data: Record<number, TagRow> }>) => {
    const rows = Object.values(event.detail?.data ?? {});
    const cells = rows.reduce((total, row) => total + Object.keys(row).length, 0);
    const dropped = rows.reduce(
      (total, row) => total + sanitizeOptionValues(row as unknown as Record<string, unknown>),
      0
    );
    writeReportedRef.current = true;
    setStatus(
      dropped > 0
        ? `Wrote ${cells - dropped} cell${cells - dropped === 1 ? '' : 's'}. Blocked ${dropped} ` +
            `out-of-list value${dropped === 1 ? '' : 's'} from the dropdown column ` +
            `(allowed: ${UNIT_OPTIONS.join(', ')}).`
        : `Wrote ${cells} cell${cells === 1 ? '' : 's'} across the selected range.`
    );
  };

  const visibleRows = Math.min(Math.max(source.length, MIN_VISIBLE_ROWS), MAX_VISIBLE_ROWS);

  return (
    <Box sx={{ p: 3, backgroundColor: theme.palette.background.paper, minHeight: '100%' }}>
      <Card sx={{ p: 3, backgroundColor: theme.palette.background.default }}>
        {/*
          The bulk-edit toolbar (a "Value" field + "Apply to selection" button)
          used to live here and has been removed — see PART 5 at the top of the
          file. Bulk range writes are now driven from inside the grid: paste for
          #2, and nothing yet for #1.
        */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3, minHeight: 40 }}>
          <Typography variant="body2" color="text.secondary">
            {status ||
              'Drag a range, then either type a value and press Enter, or Ctrl+V a single clipboard value — both fill the whole range.'}
          </Typography>
        </Stack>

        {/* requirement 4 — only mounted while something is selected */}
        {someSelected && (
          <Paper
            variant="outlined"
            sx={{
              mb: 2,
              px: 2,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              backgroundColor: theme.palette.background.neutral,
              borderColor: theme.palette.divider,
            }}
          >
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
              {selectedCount} tag{selectedCount === 1 ? '' : 's'} selected
            </Typography>
            <Button size="small" onClick={() => setSelectedIds(new Set())}>
              Clear
            </Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              onClick={() => setConfirmOpen(true)}
            >
              Delete selected
            </Button>
          </Paper>
        )}

        {/*
          `range` enables Excel-like range selection (click+drag, shift+click) and is
          also what renders the fill handle in the corner of the selection, so dragging
          it copies values across the range. The handle needs a focused cell and a
          non-readonly grid, both of which are the default.

          The wrapper owns the height because `revo-grid` is `height: 100%` with a
          300px min-height, and it carries the MUI custom-property overrides.
        */}
        {/* plain <Card> on purpose: same nesting as the reference form, so the
            16px radius + customShadows.card come from overrides/Card.ts */}
        <Card
          sx={{
            ...revoGridMuiSx(theme),
            height: HEADER_SIZE + ROW_SIZE * visibleRows,
            backgroundColor: theme.palette.background.default,
            overflow: 'hidden',
          }}
        >
          <RevoGrid
            ref={gridRef}
            theme={theme.palette.mode === 'dark' ? 'darkMaterial' : 'material'}
            columns={columns}
            source={source}
            additionalData={gridExtras}
            rowSize={ROW_SIZE}
            range={true}
            // default is already true; spelled out because it is the other half
            // of what click-drag range selection needs — the drag anchors on the
            // focused cell, so a grid that cannot focus cannot range-select
            canFocus={true}
            /**
             * Requirement #2 — paste one clipboard value into every cell of the
             * selected range. This is native, but it is NOT what the default
             * gives you: `useClipboard` defaults to the boolean `true`, and
             * isClipboardRangeFillEnabled() requires an OBJECT with
             * rangeFill === true. With the boolean, a 1x1 paste lands in the
             * focused cell only. With this, onPaste() passes the dragged range
             * as `targetRange` and getTransformedDataToApply() tiles the single
             * value across it (readonly cells skipped, so the checkbox column
             * needs no guard). Multi-cell payloads are unaffected — they still
             * tile from the focused cell as before.
             */
            useClipboard={{ rangeFill: true }}
            resize={true}
            onBeforerangeedit={handleBeforeRangeEdit}
            onBeforerange={handleBeforeRange}
            // type-to-overwrite, in grid-event order:
            //   beforeeditstart → arm (was this opened by a keystroke?)
            //   beforeedit      → cancel the single-cell write, fan out instead
            //   beforefocuslost → disarm
            onBeforeeditstart={handleBeforeEditStart}
            onBeforeedit={handleBeforeEdit}
            onBeforefocuslost={handleBeforeFocusLost}
            // not in the wrapper's typings (it is an overlay event, not a
            // revo-grid one) but the wrapper forwards any unknown on* prop to
            // addEventListener, so the cast is only a types-level escape hatch
            {...({ onBeforeapplyrange: handleBeforeApplyRange } as Record<string, unknown>)}
          />
        </Card>

        {/* requirement 8 */}
        <Stack direction="row" sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={() => void handleAddRow()}>
            Add new Tag
          </Button>
        </Stack>
      </Card>

      {/* requirement 5 — confirm before anything is removed */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete tags</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedCount} tag{selectedCount === 1 ? '' : 's'}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => void handleConfirmDelete()}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default App;
